// A signature request — staff workspace.
// Left: the documents in the request (each edited, signed and filed on its
// own), the supporting attachments, staff and parent signatures, and the
// final "file to Drive" step. Right: a live preview of everything as the
// signer will read it.
// Route: /packets/:id   (a request is started at /packets/new — SignatureRequestNew)

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase, addDocument } from '../lib/supabase';
import PacketPdfFrame from '../components/signing/PacketPdfFrame';
import FieldPlacer from '../components/signing/FieldPlacer';
import {
  FORM_FIELDS, PARTY, REQUEST_KINDS, UPLOAD_PARTIES, GENERAL_PARTIES, DOC_SHORT_NAME, generalClientStub, getPacketBundle, saveFormData,
  signSlot, isPacketComplete, finalizePacket, tokenFacts, setPacketLanguage, setUploadFields, removeForm,
  recordFormFile, deleteOrVoidPacket, voidPacket, removeAttachment, missingRequired,
} from '../lib/documents/packets';
import { LANGS, normalizeLang, formTitle } from '../lib/documents/i18n';
import { generateAndUploadPacket as genUpload, fileRequestToDrive, isGappPacket } from '../lib/documents/generatePacketPdf';
import { isUploadForm } from '../lib/documents/gappOverlay';
import { sendSigningLink } from '../lib/documents/signingLinks';
import { cycleFolder } from '../lib/paCycles';
import OpenLink from '../components/OpenLink';
import ClientTrail, { tabCrumbs } from '../components/ClientTrail';
import { CRM_EMAIL } from '../lib/org';

const INTERNAL_ROLES = [PARTY.RN, PARTY.AGENCY, PARTY.ADMIN, PARTY.WITNESS];
const ROLE_LABEL = { client: 'parent', physician: 'physician', external: 'recipient' };
const STATUS_STYLE = {
  completed: { bg: 'var(--success-bg)', fg: 'var(--success)' },
  partially_signed: { bg: 'var(--warn-bg)', fg: 'var(--warn)' },
  sent: { bg: 'var(--warn-bg)', fg: 'var(--warn)' },
  draft: { bg: 'var(--bg2)', fg: 'var(--text2)' },
  voided: { bg: 'var(--danger-bg)', fg: 'var(--danger)' },
};

export default function PacketBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [packet, setPacket] = useState(null);
  const [client, setClient] = useState(null);
  const [cycle, setCycle] = useState(null);
  const [forms, setForms] = useState([]);
  const [slots, setSlots] = useState([]);
  const [tokens, setTokens] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [files, setFiles] = useState({});          // Drive file id → bytes, for uploaded documents
  const [lang, setLang] = useState('en');
  const [formState, setFormState] = useState({});
  const [openForm, setOpenForm] = useState(null);  // form_key whose fields are expanded
  const [placing, setPlacing] = useState(null);    // upload form being placed
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  const [progress, setProgress] = useState('');
  const [emails, setEmails] = useState({});
  const [staffNames, setStaffNames] = useState({});
  const [answers, setAnswers] = useState({});        // in-person agree / disagree, by role
  const [armed, setArmed] = useState('');          // two-click confirm for delete / void

  // ── load ──
  useEffect(() => {
    (async () => {
      try {
        const bundle = await getPacketBundle(id);
        if (!bundle.packet) throw new Error(`Request ${id} not found.`);
        setPacket(bundle.packet); setForms(bundle.forms); setSlots(bundle.slots);
        setTokens(bundle.tokens || []); setAttachments(bundle.attachments || []);
        setLang(normalizeLang(bundle.packet.language));
        const fs = {};
        bundle.forms.forEach((f) => { fs[f.form_key] = f.field_data || {}; });
        setFormState(fs);
        // A client-less request renders against a stub so nothing downstream has to care.
        const { data: c } = bundle.packet.client_id
          ? await supabase.from('clients').select('*').eq('id', bundle.packet.client_id).single()
          : { data: generalClientStub(bundle.packet) };
        setClient(c);
        if (bundle.packet.cycle_id) {
          const { data: cy } = await supabase.from('pa_cycles').select('*').eq('id', bundle.packet.cycle_id).single();
          setCycle(cy || null);
        }
        const infoKeys = (FORM_FIELDS.client_info || []).map((f) => f.key);
        setFormState((prev) => ({ ...prev, client_info: Object.fromEntries(infoKeys.map((k) => [k, c?.[k] ?? ''])) }));
        const defaultEmail = c?.guardian_email || c?.emergency_email || '';
        if (defaultEmail) setEmails((e) => ({ client: defaultEmail, physician: '', ...e }));
        if (bundle.packet.recipient_email) setEmails((e) => ({ ...e, external: bundle.packet.recipient_email }));
        if (bundle.packet.recipient_name) setStaffNames((s) => ({ ...s, external: bundle.packet.recipient_name }));
        // Who was named to sign a staff line when the request was made (the
        // LON's agency line, chosen on the decision): prefilled, still editable.
        const named = Object.assign({}, ...bundle.forms.map((f) => (f.field_data || {}).signerNames || {}));
        if (Object.keys(named).length) setStaffNames((s) => ({ ...s, ...named }));
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data: prof } = await supabase.from('profiles').select('full_name').eq('id', user.id).single();
          const me = prof?.full_name || '';
          if (me) setStaffNames((s) => ({ rn: me, agency: me, administrator: me, witness: me, ...s }));
        }
        // bytes of uploaded documents, for the preview and the final render
        for (const f of bundle.forms.filter((x) => x.source_file_id)) {
          supabase.functions.invoke('drive-file', { body: { action: 'download', fileId: f.source_file_id } }).then(({ data }) => {
            if (data?.success) setFiles((m) => ({ ...m, [f.source_file_id]: Uint8Array.from(atob(data.base64), (ch) => ch.charCodeAt(0)) }));
          }).catch(() => {});
        }
      } catch (e) {
        console.error('[PacketBuilder] load failed:', e);
        setMsg(`Error: ${e.message}`);
      } finally { setLoading(false); }
    })();
  }, [id]);

  const refresh = useCallback(async () => {
    const [{ data: p }, { data: sl }, { data: tk }] = await Promise.all([
      supabase.from('document_packets').select('*').eq('id', id).single(),
      supabase.from('signature_slots').select('*').eq('packet_id', id).order('sort_order'),
      supabase.from('parent_portal_tokens').select('*').eq('packet_id', id).order('created_at'),
    ]);
    if (p) setPacket(p); if (sl) setSlots(sl); if (tk) setTokens(tokenFacts(tk));
  }, [id]);
  useEffect(() => {
    if (!packet || ['completed', 'voided'].includes(packet.status)) return undefined;
    const t = setInterval(refresh, 15000);
    return () => clearInterval(t);
  }, [packet, refresh]);

  const previewForms = useMemo(() => forms.map((f) => ({ ...f, field_data: formState[f.form_key] || f.field_data || {} })), [forms, formState]);
  const previewClient = useMemo(() => (client ? { ...client, ...(formState.client_info || {}) } : null), [client, formState.client_info]);

  const switchLang = async (next) => {
    if (next === lang) return;
    setBusy('lang');
    try { await setPacketLanguage(packet.id, next); setLang(next); setPacket((p) => ({ ...p, language: next })); }
    catch (e) { setMsg(`Could not switch language: ${e.message}`); }
    setBusy('');
  };
  const setField = (formKey, key, value) => setFormState((s) => ({ ...s, [formKey]: { ...(s[formKey] || {}), [key]: value } }));

  const saveForm = async (form) => {
    setBusy(form.form_key);
    try {
      const fd = formState[form.form_key] || {};
      if (form.form_key === 'client_info' && packet?.client_id) {
        const infoKeys = (FORM_FIELDS.client_info || []).map((f) => f.key);
        const payload = {};
        infoKeys.forEach((k) => { payload[k] = fd[k] === '' ? null : fd[k]; });
        const { error } = await supabase.from('clients').update(payload).eq('id', packet.client_id);
        if (error) throw error;
        setClient((c) => ({ ...c, ...payload }));
      }
      await saveFormData(form.id, fd);
      if (form.form_key === 'form_9' && fd.mfmcDetermination && packet?.client_id) {
        await supabase.from('clients').update({ mfmc_status: fd.mfmcDetermination, mfmc_date: fd.mfmcDate || new Date().toISOString().slice(0, 10) }).eq('id', packet.client_id);
      }
      if (form.form_key === 'form_2' && packet?.client_id) {
        const hasContent = !!(String(fd.assessmentSummary || '').trim() || (fd.adls || []).length || (fd.risks || []).length);
        if (hasContent) {
          const today = new Date().toISOString().slice(0, 10);
          await supabase.from('clients').update({ nurse_assessment_complete: true, nurse_assessment_date: today }).eq('id', packet.client_id);
          setClient((c) => (c ? { ...c, nurse_assessment_complete: true, nurse_assessment_date: today } : c));
        }
      }
      setMsg(`Saved ${form.form_title}.`);
    } catch (e) { setMsg(`Save failed: ${e.message}`); }
    setBusy('');
  };

  // The questions placed for a party on an uploaded document — the LON's
  // agree / disagree — which a signer answers before signing it, in person
  // here as much as by link.
  const questionsFor = (formKey, role) => {
    const q = {};
    (((formState[formKey] || {}).fields) || []).filter((f) => f.kind === 'choice' && f.party === role && f.group).forEach((f) => { (q[f.group] = q[f.group] || []).push(f.value); });
    return q;
  };
  const roleAsks = (role) => slots.some((s) => s.party_role === role && Object.keys(questionsFor(s.form_key, role)).length);

  const signRole = async (role) => {
    const name = (staffNames[role] || '').trim();
    if (!name) { setMsg('Type the signer’s full name first.'); return; }
    if (roleAsks(role) && !answers[role]) { setMsg('Mark whether they agree or disagree with the hours first.'); return; }
    setBusy(`sign_${role}`);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const toSign = slots.filter((s) => s.party_role === role && !s.signed_at);
      const choicesFor = (slot) => (Object.keys(questionsFor(slot.form_key, role)).length ? { agreement: answers[role] } : undefined);
      for (const slot of toSign) await signSlot(slot.id, name, { byUser: user?.id, choices: choicesFor(slot) });
      const ts = new Date().toISOString();
      const ids = new Set(toSign.map((s) => s.id));
      setSlots((arr) => arr.map((s) => (ids.has(s.id) ? { ...s, signed_name: name, signed_at: ts, ...(choicesFor(s) ? { choices: choicesFor(s) } : {}) } : s)));
      setMsg(`Signed ${toSign.length} place${toSign.length === 1 ? '' : 's'} as ${name}.`);
    } catch (e) { setMsg(`Sign failed: ${e.message}`); }
    setBusy('');
  };

  const sendLink = async (role, email) => {
    setBusy(`link_${role}`);
    try {
      const { to } = await sendSigningLink({ packet, client, role, email, lang, recipientName: role === PARTY.EXTERNAL ? packet.recipient_name : undefined });
      refresh();
      setMsg(`Signing link emailed to ${to} (cc ${CRM_EMAIL}).`);
    } catch (e) { setMsg(`Send failed: ${e.message}`); }
    setBusy('');
  };

  // Every document is rendered and filed on its own; the certificate last.
  const finalize = async () => {
    if (!isPacketComplete(slots)) { setMsg('All required signatures must be collected first.'); return; }
    setBusy('finalize'); setMsg('');
    try {
      for (const f of forms) if (!isUploadForm(f.form_key)) await saveFormData(f.id, formState[f.form_key] || {});
      if (!isGappPacket(packet.packet_type)) {
        const res = await genUpload({ packet, client, forms, slots, tokens, subfolder: cycleFolder(cycle, packet) });
        await finalizePacket(packet.id, res);
        setPacket((p) => ({ ...p, status: 'completed', final_pdf_url: res.webViewLink }));
        setMsg('Filed to Drive.');
        setBusy(''); return;
      }
      const subfolder = cycleFolder(cycle, packet);
      const byKey = Object.fromEntries(forms.map((f) => [f.form_key, f]));
      const r = await fileRequestToDrive({
        packet: { ...packet, completed_at: packet.completed_at || new Date().toISOString() }, client, forms, slots, tokens, files, subfolder,
        onProgress: async (key, up) => {
          setProgress(`Filed ${key === 'certificate' ? 'signature certificate' : (DOC_SHORT_NAME[key] || byKey[key]?.form_title || key)}…`);
          if (key !== 'certificate' && byKey[key]) {
            await recordFormFile(byKey[key].id, up);
            setForms((arr) => arr.map((f) => (f.id === byKey[key].id ? { ...f, final_pdf_id: up.fileId, final_pdf_url: up.webViewLink } : f)));
          }
        },
      });
      // supporting documents ride along into the same folder
      for (const a of attachments) {
        if (a.copied_file_id || !r.folderId) continue;
        setProgress(`Copying ${a.name}…`);
        const { data } = await supabase.functions.invoke('drive-file', { body: { action: 'copy', fileId: a.drive_file_id, toFolderId: r.folderId } });
        if (data?.success) {
          await supabase.from('packet_attachments').update({ copied_file_id: data.id, copied_url: data.webViewLink }).eq('id', a.id);
          setAttachments((arr) => arr.map((x) => (x.id === a.id ? { ...x, copied_file_id: data.id, copied_url: data.webViewLink } : x)));
        }
      }
      const final = r.certificate || r.filed[r.filed.length - 1];
      await finalizePacket(packet.id, { fileId: final?.fileId, webViewLink: final?.webViewLink });
      try {
        if (packet.client_id) await addDocument({ client_id: packet.client_id, document_name: `${packet.title || 'Signature request'} — ${r.filed.length} documents, signed ${new Date().toLocaleDateString('en-US')}`, document_type: 'Signing Packets', drive_url: final?.webViewLink });
      } catch (_) { /* link on the Documents tab is best-effort */ }
      setPacket((p) => ({ ...p, status: 'completed', completed_at: new Date().toISOString(), final_pdf_url: final?.webViewLink }));
      setMsg(`Filed ${r.filed.length} document${r.filed.length === 1 ? '' : 's'}${r.certificate ? ' and the signature certificate' : ''} to ${subfolder}.`);
    } catch (e) { setMsg(`Filing failed: ${e.message}`); }
    setProgress(''); setBusy('');
  };

  const removeRequest = async () => {
    if (armed !== 'remove') { setArmed('remove'); setTimeout(() => setArmed((a) => (a === 'remove' ? '' : a)), 6000); return; }
    setBusy('remove');
    try {
      const what = anySigned ? 'voided' : await deleteOrVoidPacket(packet.id);
      if (anySigned) await voidPacket(packet.id);
      navigate(packet.client_id ? `/clients/${packet.client_id}?tab=documents&sub=esign` : '/esign', { replace: true, state: { flash: what === 'deleted' ? 'Draft deleted.' : 'Request voided.' } });
    } catch (e) { setMsg(`Could not remove: ${e.message}`); setBusy(''); }
  };

  const dropForm = async (form) => {
    try {
      await removeForm(packet.id, form);
      setForms((arr) => arr.filter((f) => f.id !== form.id));
      setSlots((arr) => arr.filter((s) => s.form_key !== form.form_key));
    } catch (e) { setMsg(e.message); }
  };

  if (loading) return <div style={{ padding: 24 }}>Loading request…</div>;
  if (!packet) return <div style={{ padding: 24 }}>{msg || 'Request not found.'}</div>;

  const gappNow = isGappPacket(packet.packet_type);
  const anySigned = slots.some((s) => s.signed_at);
  const complete = isPacketComplete(slots);
  // The send guard: nothing goes to the parent while a coordinator field is empty.
  const missing = gappNow && packet.client_id ? missingRequired({ client: previewClient || client, forms: forms.filter((f) => !isUploadForm(f.form_key)).map((f) => ({ form_key: f.form_key, field_data: formState[f.form_key] || f.field_data || {} })), gapp: true }) : [];
  const blocked = missing.length > 0;
  const done = packet.status === 'completed';
  const req = slots.filter((s) => s.required);
  const signedN = req.filter((s) => s.signed_at).length;
  const internalSlots = slots.filter((s) => INTERNAL_ROLES.includes(s.party_role));
  const externalRoles = [...new Set(slots.filter((s) => !INTERNAL_ROLES.includes(s.party_role)).map((s) => s.party_role))];
  const title = REQUEST_KINDS[packet.request_kind]?.label || packet.title || 'Signature request';
  const st = STATUS_STYLE[packet.status] || STATUS_STYLE.draft;
  const gapp = isGappPacket(packet.packet_type);

  // The way back: the client's GAPP Submissions when the request belongs to a
  // submission, their e-Signatures otherwise; e-Signatures for one about no client.
  const trail = packet.client_id
    ? <ClientTrail client={client?.id ? client : null} clientId={packet.client_id} sticky={false}
        crumbs={[...tabCrumbs(packet.client_id, 'documents', cycle ? 'submissions' : 'esign'), ...(cycle ? [{ label: cycle.label }] : []), { label: title }]} />
    : <ClientTrail sticky={false} root={[{ label: 'e-Signatures', to: '/esign' }]} crumbs={[{ label: packet.subject || title }]} />;

  return (
    <div style={{ padding: '0 12px' }}>
    {trail}
    <div style={{ display: 'flex', gap: 16, height: 'calc(100vh - 170px)', paddingBottom: 12 }}>
      {/* LEFT */}
      <div style={{ flex: '0 0 46%', overflowY: 'auto', paddingRight: 8 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>{title}</h2>
              {lang === 'es' && <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 5px', borderRadius: 4, background: 'var(--accent-bg)', color: 'var(--accent)', border: '1px solid var(--accent-border)' }}>ES</span>}
              <span style={{ padding: '1px 9px', borderRadius: 10, fontSize: 11, fontWeight: 700, background: st.bg, color: st.fg }}>{packet.status.replace('_', ' ')}</span>
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--text3)', marginTop: 2 }}>
              {packet.client_id
                ? <button type="button" onClick={() => navigate(`/clients/${packet.client_id}?tab=documents&sub=esign`)} style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', color: 'var(--accent)', cursor: 'pointer' }}>{client?.last_name}, {client?.first_name} · {client?.client_id}</button>
                : <button type="button" onClick={() => navigate('/esign')} style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', color: 'var(--accent)', cursor: 'pointer' }}>{packet.subject || 'Not tied to a client'}{packet.recipient_name ? ` · to ${packet.recipient_name}` : ''}</button>}
              {cycle && <> · {cycle.label}</>} · files to <b>{cycleFolder(cycle, packet)}</b>
            </div>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 4, display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ width: 90, height: 5, background: 'var(--border)', borderRadius: 3, overflow: 'hidden', display: 'inline-block' }}><span style={{ display: 'block', width: `${req.length ? (signedN / req.length) * 100 : 0}%`, height: '100%', background: complete ? 'var(--success)' : 'var(--accent)' }} /></span>
              <b style={{ color: complete ? 'var(--success)' : 'var(--text2)' }}>{signedN} / {req.length} signatures</b>
              {tokens.length > 0 && <span>· link sent {new Date(tokens[tokens.length - 1].created_at).toLocaleDateString()}{tokens[tokens.length - 1].accessed_at ? `, opened ${new Date(tokens[tokens.length - 1].accessed_at).toLocaleDateString()}` : ', not opened yet'}</span>}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', border: '1px solid var(--border2)', borderRadius: 6, overflow: 'hidden' }} title="Language of the documents, email and signing page">
              {LANGS.map((l) => (
                <button key={l.code} disabled={busy === 'lang' || done} onClick={() => switchLang(l.code)}
                  style={{ border: 'none', padding: '4px 10px', fontSize: 12, cursor: 'pointer', minWidth: 64, background: lang === l.code ? 'var(--accent)' : 'transparent', color: lang === l.code ? '#fff' : 'var(--text2)', fontWeight: lang === l.code ? 600 : 400 }}>{l.short}</button>
              ))}
            </div>
            <button className="btn btn-outline btn-sm" onClick={refresh}>↻</button>
            {!done && packet.status !== 'voided' && (
              <button className="btn btn-ghost btn-sm" disabled={busy === 'remove'} onClick={removeRequest} style={{ color: 'var(--danger)' }}>
                {armed === 'remove' ? (anySigned ? 'Click again to void' : 'Click again to delete') : anySigned ? 'Void request' : 'Delete draft'}
              </button>
            )}
          </div>
        </div>
        {msg && <div style={{ background: 'var(--bg2)', padding: 8, borderRadius: 6, margin: '8px 0', fontSize: 13 }}>{msg}</div>}

        {/* documents */}
        <div style={cardStyle}>
          <h3 style={{ margin: '0 0 4px', fontSize: 14 }}>Documents</h3>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 6 }}>Each one is signed on its own and comes back as its own file.</div>
          {forms.map((form) => {
            const fSlots = slots.filter((s) => s.form_key === form.form_key);
            const fDone = fSlots.length > 0 && fSlots.every((s) => s.signed_at);
            const upload = isUploadForm(form.form_key);
            const fields = FORM_FIELDS[form.form_key] || [];
            const open = openForm === form.form_key;
            return (
              <div key={form.id} id={`form-${form.form_key}`} style={{ borderTop: '1px solid var(--border)', padding: '8px 0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 600 }}>{upload ? form.form_title : formTitle(lang, form.form_key, form.form_title)}</div>
                    <div style={{ fontSize: 11.5, display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 2 }}>
                      {fSlots.length === 0 && <span style={{ color: 'var(--text3)' }}>no signature · filled from the record</span>}
                      {fSlots.map((s) => <span key={s.id} style={{ color: s.signed_at ? 'var(--success)' : 'var(--text3)' }}>{s.signed_at ? '✓' : '○'} {s.signer_label}{s.signed_at ? ` · ${new Date(s.signed_at).toLocaleDateString()}` : ''}</span>)}
                    </div>
                  </div>
                  <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    {form.final_pdf_url
                      ? <OpenLink href={form.final_pdf_url}>PDF</OpenLink>
                      : <span style={{ fontSize: 10.5, fontWeight: 700, padding: '1px 8px', borderRadius: 10, background: fDone ? 'var(--success-bg)' : 'var(--bg2)', color: fDone ? 'var(--success)' : 'var(--text2)' }}>{fDone ? 'signed' : fSlots.length ? 'waiting' : 'ready'}</span>}
                    {!done && upload && <button className="btn btn-outline btn-sm" onClick={() => setPlacing(form)}>Place fields</button>}
                    {!done && !upload && fields.length > 0 && <button className="btn btn-ghost btn-sm" onClick={() => setOpenForm(open ? null : form.form_key)}>{open ? 'Hide' : 'Edit'}</button>}
                    {!done && !fSlots.some((s) => s.signed_at) && forms.length > 1 && <button className="btn btn-ghost btn-sm" title="Remove from this request" onClick={() => dropForm(form)}>×</button>}
                  </span>
                </div>
                {open && !upload && (
                  <div style={{ marginTop: 8, padding: '8px 10px', background: 'var(--bg2)', borderRadius: 6 }}>
                    {fields.map((field) => (
                      <FieldEditor key={field.key} field={field} value={(formState[form.form_key] || {})[field.key]} onChange={(v) => setField(form.form_key, field.key, v)} />
                    ))}
                    <button disabled={busy === form.form_key} onClick={() => saveForm(form)} style={btnStyle}>{busy === form.form_key ? 'Saving…' : 'Save'}</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* attachments */}
        {attachments.length > 0 && (
          <div style={cardStyle}>
            <h3 style={{ margin: '0 0 4px', fontSize: 14 }}>Attached, not signed</h3>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 6 }}>Supporting documents, copied into the folder when the request completes.</div>
            {attachments.map((a) => (
              <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 8, borderTop: '1px solid var(--border)', padding: '7px 0', fontSize: 13 }}>
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.name} <span style={{ fontSize: 11.5, color: 'var(--text3)' }}>· {a.source_path || 'Drive'}{a.copied_file_id ? ' · copied' : ''}</span></span>
                {a.copied_url ? <OpenLink href={a.copied_url}>Open</OpenLink> : <OpenLink href={`https://drive.google.com/file/d/${a.drive_file_id}/view`}>Open</OpenLink>}
                {!done && <button className="btn btn-ghost btn-sm" onClick={async () => { await removeAttachment(a.id); setAttachments((arr) => arr.filter((x) => x.id !== a.id)); }}>×</button>}
              </div>
            ))}
          </div>
        )}

        {/* staff signatures */}
        {internalSlots.length > 0 && !done && (
          <div style={cardStyle}>
            <h3 style={{ margin: '0 0 8px', fontSize: 14 }}>Staff signatures</h3>
            {[...new Set(internalSlots.map((s) => s.party_role))].map((role) => {
              const roleSlots = slots.filter((s) => s.party_role === role);
              const allSigned = roleSlots.every((s) => s.signed_at);
              const label = (roleSlots[0] || {}).signer_label || role;
              return (
                <div key={role} style={{ borderTop: '1px solid var(--border)', paddingTop: 8, marginTop: 8 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{label} · {roleSlots.length} place{roleSlots.length === 1 ? '' : 's'}</div>
                  {allSigned ? <div style={{ color: 'var(--text)', fontFamily: 'cursive', fontSize: 18 }}>{roleSlots[0].signed_name} ✓</div> : (
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input placeholder="Type full legal name" value={staffNames[role] || ''} onChange={(e) => setStaffNames((s) => ({ ...s, [role]: e.target.value }))} style={{ flexGrow: 1, padding: 7, border: '1px solid var(--border2)', borderRadius: 4, fontSize: 13 }} />
                      <button disabled={busy === `sign_${role}` || !staffNames[role]?.trim()} onClick={() => signRole(role)} style={btnSm}>{busy === `sign_${role}` ? 'Signing…' : 'Adopt & sign'}</button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* parent / external */}
        {externalRoles.length > 0 && !done && (
          <div style={cardStyle}>
            <h3 style={{ margin: '0 0 8px', fontSize: 14 }}>{packet.client_id ? 'Parent / external signatures' : 'Recipient signature'}</h3>
            {blocked && (
              <div style={{ border: '1px solid var(--warn-border)', background: 'var(--warn-bg)', borderRadius: 8, padding: '8px 12px', fontSize: 12.5, marginBottom: 8 }}>
                <b style={{ color: 'var(--warn)' }}>Fill {missing.length} field{missing.length === 1 ? '' : 's'} before sending:</b>
                <ul style={{ margin: '4px 0 0', paddingLeft: 18, color: 'var(--text2)' }}>
                  {missing.map((m) => (
                    <li key={`${m.form_key}:${m.key}`}>
                      {m.form_key === 'client' ? <>Client record — {m.label} · <button type="button" onClick={() => navigate(`/clients/${packet.client_id}/edit`)} style={linkBtn}>Edit client</button></>
                        : <>{(forms.find((f) => f.form_key === m.form_key) || {}).form_title?.split(' — ')[0] || m.form_key} — {m.label} · <button type="button" onClick={() => { setOpenForm(m.form_key); document.getElementById(`form-${m.form_key}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }); }} style={linkBtn}>Edit</button></>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {externalRoles.map((role) => {
              const roleSlots = slots.filter((s) => s.party_role === role);
              const allSigned = roleSlots.every((s) => s.signed_at);
              const label = (roleSlots[0] || {}).signer_label || role;
              return (
                <div key={role} style={{ borderTop: '1px solid var(--border)', paddingTop: 8, marginTop: 8 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{label} · {roleSlots.length} place{roleSlots.length === 1 ? '' : 's'}</div>
                  {allSigned ? <div style={{ color: 'var(--text)', fontFamily: 'cursive', fontSize: 18 }}>{roleSlots[0].signed_name} ✓</div> : (
                    <>
                      <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                        <input placeholder="Type full legal name (sign in person)" value={staffNames[role] || ''} onChange={(e) => setStaffNames((s) => ({ ...s, [role]: e.target.value }))} style={{ flexGrow: 1, padding: 7, border: '1px solid var(--border2)', borderRadius: 4, fontSize: 13 }} />
                        {roleAsks(role) && (
                          <select className="form-select" value={answers[role] || ''} onChange={(e) => setAnswers((a) => ({ ...a, [role]: e.target.value }))} style={{ fontSize: 13, width: 'auto' }} aria-label="Agree or disagree with the hours">
                            <option value="">Agree / disagree…</option><option value="agree">Agrees</option><option value="disagree">Disagrees</option>
                          </select>
                        )}
                        <button disabled={blocked || busy === `sign_${role}` || !staffNames[role]?.trim()} onClick={() => signRole(role)} style={btnSm}>{busy === `sign_${role}` ? 'Signing…' : 'Sign in person'}</button>
                      </div>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <input type="email" placeholder={`${ROLE_LABEL[role] || role} email`} value={emails[role] || ''} onChange={(e) => setEmails((s) => ({ ...s, [role]: e.target.value }))} style={{ flexGrow: 1, padding: 7, border: '1px solid var(--border2)', borderRadius: 4, fontSize: 13 }} />
                        <button disabled={blocked || busy === `link_${role}`} onClick={() => sendLink(role, emails[role])} style={btnSm}>{busy === `link_${role}` ? 'Sending…' : tokens.some((t) => t.party_role === role) ? 'Resend link' : 'Email link'}</button>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* file to drive */}
        <div style={cardStyle}>
          {done ? (
            <div style={{ fontSize: 13 }}>
              <b style={{ color: 'var(--success)' }}>✓ Filed</b> to {cycleFolder(cycle, packet)} on {new Date(packet.completed_at).toLocaleDateString()}.
              {packet.final_pdf_url && <> Signature certificate: <a href={packet.final_pdf_url} target="_blank" rel="noreferrer">open</a>.</>}
              {!gapp && packet.final_pdf_url && <> <a href={packet.final_pdf_url} target="_blank" rel="noreferrer">Open the PDF</a>.</>}
            </div>
          ) : (
            <>
              <button disabled={!complete || busy === 'finalize'} onClick={finalize} style={{ ...btnStyle, marginTop: 0, background: complete ? 'var(--accent)' : 'var(--border2)' }}>
                {busy === 'finalize' ? (progress || 'Filing…') : gapp ? `File ${forms.length} document${forms.length === 1 ? '' : 's'} to Drive` : 'Generate final PDF → Drive'}
              </button>
              {!complete && <p style={{ fontSize: 12, color: 'var(--warn)', margin: '6px 0 0' }}>All signatures must be in before filing.</p>}
              {complete && gapp && <p style={{ fontSize: 12, color: 'var(--text3)', margin: '6px 0 0' }}>One PDF per document plus the signature certificate, into {cycleFolder(cycle, packet)}.</p>}
            </>
          )}
        </div>
      </div>

      {/* RIGHT: preview */}
      <div style={{ flexGrow: 1 }}>
        {previewClient && <PacketPdfFrame packet={packet} client={previewClient} forms={previewForms} slots={slots} tokens={tokens} files={files} />}
      </div>

      {placing && (
        <div className="modal-overlay" onMouseDown={(e) => e.target === e.currentTarget && setPlacing(null)}>
          <div className="modal" style={{ maxWidth: 1000, width: '96vw' }} onMouseDown={(e) => e.stopPropagation()}>
            <div className="modal-header"><div className="modal-title">Place fields — {placing.form_title}</div><button className="btn btn-ghost btn-sm" onClick={() => setPlacing(null)}>✕</button></div>
            <div className="modal-body" style={{ maxHeight: '75vh', overflow: 'auto' }}>
              {files[placing.source_file_id]
                ? <FieldPlacer bytes={files[placing.source_file_id]} fields={(formState[placing.form_key] || {}).fields || []} onChange={(f) => setField(placing.form_key, 'fields', f)} parties={packet.client_id ? UPLOAD_PARTIES : GENERAL_PARTIES} defaultParty={packet.client_id ? PARTY.CLIENT : PARTY.EXTERNAL} />
                : <div style={{ fontSize: 13, color: 'var(--text3)' }}>Fetching the file from Drive…</div>}
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setPlacing(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={async () => {
                try {
                  await setUploadFields(packet.id, placing, (formState[placing.form_key] || {}).fields || []);
                  setForms((arr) => arr.map((f) => (f.id === placing.id ? { ...f, field_data: { ...f.field_data, fields: (formState[placing.form_key] || {}).fields || [] } } : f)));
                  const { data: sl } = await supabase.from('signature_slots').select('*').eq('packet_id', packet.id).order('sort_order');
                  if (sl) setSlots(sl);
                  setPlacing(null);
                } catch (e) { setMsg(`Could not save fields: ${e.message}`); }
              }}>Save fields</button>
            </div>
          </div>
        </div>
      )}
    </div>
    </div>
  );
}

function FieldEditor({ field, value, onChange }) {
  const wrap = { marginBottom: 8 };
  const label = <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 2 }}>{field.label}</label>;
  const inputStyle = { width: '100%', padding: 6, border: '1px solid var(--border2)', borderRadius: 4, fontSize: 13, boxSizing: 'border-box' };

  if (field.type === 'textarea') return <div style={wrap}>{label}<textarea rows={3} style={inputStyle} value={value || ''} onChange={(e) => onChange(e.target.value)} /></div>;
  if (field.type === 'select') return <div style={wrap}>{label}<select style={inputStyle} value={value || ''} onChange={(e) => onChange(e.target.value)}><option value="">—</option>{field.options.map((o) => <option key={o} value={o}>{o}</option>)}</select></div>;
  if (field.type === 'checks') return (
    <div style={wrap}>{label}
      {field.options.map((o) => (
        <label key={o} style={{ display: 'block', fontSize: 13 }}>
          <input type="checkbox" checked={(value || []).includes(o)}
            onChange={(e) => onChange(e.target.checked ? [...(value || []), o] : (value || []).filter((x) => x !== o))} /> {o}
        </label>
      ))}
    </div>
  );
  if (field.type === 'table') return <TableEditor field={field} value={value} onChange={onChange} label={label} />;
  return <div style={wrap}>{label}<input style={inputStyle} value={value || ''} onChange={(e) => onChange(e.target.value)} /></div>;
}

// Inline row editor: one row per entry, a cell per column, add/remove controls.
function TableEditor({ field, value, onChange, label }) {
  const rows = Array.isArray(value) ? value : [];
  const { cols, colLabels = cols, colTypes = [], colOptions = {} } = field;

  const update = (rowIdx, colKey, v) =>
    onChange(rows.map((r, i) => (i === rowIdx ? { ...r, [colKey]: v } : r)));
  const addRow = () => onChange([...rows, cols.reduce((o, c) => ({ ...o, [c]: '' }), {})]);
  const removeRow = (rowIdx) => onChange(rows.filter((_, i) => i !== rowIdx));

  const cell = { border: '1px solid var(--border2)', padding: 0 };
  const cellInput = { width: '100%', border: 'none', padding: '6px 8px', fontSize: 13, boxSizing: 'border-box', background: 'transparent' };
  const th = { background: 'var(--navy)', color: '#fff', fontSize: 11, fontWeight: 700, padding: '5px 8px', textAlign: 'left' };

  return (
    <div style={{ marginBottom: 10 }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{field.label}</label>
      <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
        <thead>
          <tr>
            {colLabels.map((cl, i) => <th key={i} style={th}>{cl}</th>)}
            <th style={{ ...th, width: 34, textAlign: 'center' }} aria-label="remove" />
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={cols.length + 1} style={{ ...cell, padding: 8, fontSize: 12, color: 'var(--text3)', textAlign: 'center' }}>No rows yet — click “Add row”.</td></tr>
          )}
          {rows.map((row, ri) => (
            <tr key={ri}>
              {cols.map((c, ci) => (
                <td key={c} style={cell}>
                  {colTypes[ci] === 'select'
                    ? <select style={cellInput} value={row[c] || ''} onChange={(e) => update(ri, c, e.target.value)}>
                        <option value="">—</option>
                        {(colOptions[c] || []).map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    : <input style={cellInput} value={row[c] || ''} onChange={(e) => update(ri, c, e.target.value)} />}
                </td>
              ))}
              <td style={{ ...cell, textAlign: 'center' }}>
                <button type="button" onClick={() => removeRow(ri)} title="Remove row"
                  style={{ border: 'none', background: 'transparent', color: 'var(--danger)', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>×</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button type="button" onClick={addRow}
        style={{ marginTop: 6, background: '#fff', color: 'var(--accent)', border: '1px solid var(--accent)', borderRadius: 4, padding: '4px 10px', fontSize: 12, cursor: 'pointer' }}>
        + Add row
      </button>
    </div>
  );
}

const linkBtn = { background: 'none', border: 'none', padding: 0, font: 'inherit', fontSize: 'inherit', color: 'var(--accent)', cursor: 'pointer', textDecoration: 'underline' };
const cardStyle = { background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 8, padding: 12, marginTop: 12 };
const btnStyle = { background: 'var(--accent)', color: '#fff', border: 'none', padding: '8px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 13, marginTop: 8 };
const btnSm = { background: 'var(--accent)', color: '#fff', border: 'none', padding: '4px 10px', borderRadius: 4, cursor: 'pointer', fontSize: 12 };
